import type { Coding, QuestionnaireResponseItemAnswer } from "@medplum/fhirtypes";

import {
  NO_DISPLAY,
  YES_DISPLAY,
  type YesNoDisplay,
} from "@/intake-forms/constants/yes-no-display";
import {
  readYesNoDisplayFromAnswer,
  yesNoDisplayToLoincCoding,
} from "@/intake-forms/utils/yes-no-questionnaire-answer";

export const OTHER_DISPLAY = "Other";

export type YesNoOtherDisplay = YesNoDisplay | typeof OTHER_DISPLAY;

/** Form-specific Coding for the "Other" radio choice. */
export type YesNoOtherCoding = Pick<Coding, "system" | "code"> & {
  display?: string;
};

/** Maps Yes/No to LOINC Coding and Other to the provided coding. */
export function yesNoOtherDisplayToCoding(
  display: string | null | undefined,
  otherCoding: YesNoOtherCoding,
): Coding | undefined {
  if (display === YES_DISPLAY || display === NO_DISPLAY) {
    return yesNoDisplayToLoincCoding(display) ?? undefined;
  }
  if (display === OTHER_DISPLAY) {
    return {
      system: otherCoding.system,
      code: otherCoding.code,
      display: otherCoding.display ?? OTHER_DISPLAY,
    };
  }
  return undefined;
}

/**
 * Reads Yes/No/Other from a questionnaire answer.
 * Uses Yes/No helpers first, then matches Other by code, display, or valueString.
 */
export function readYesNoOtherDisplayFromAnswer(
  answer: QuestionnaireResponseItemAnswer | undefined,
  otherCoding: Pick<YesNoOtherCoding, "code"> & { display?: string },
): YesNoOtherDisplay | null {
  if (!answer) {
    return null;
  }

  const yesNoDisplay = readYesNoDisplayFromAnswer(answer);
  if (yesNoDisplay) {
    return yesNoDisplay;
  }

  const otherDisplay = otherCoding.display ?? OTHER_DISPLAY;
  const coding = answer.valueCoding;
  if (coding?.display === otherDisplay || coding?.code === otherCoding.code) {
    return OTHER_DISPLAY;
  }

  if (answer.valueString === otherDisplay) {
    return OTHER_DISPLAY;
  }

  return null;
}

export function isOtherDisplaySelected(display: string | null | undefined): boolean {
  return display === OTHER_DISPLAY;
}
